import './Meditation.scss'

import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import Drawer from 'react-bottom-drawer'
import { Button } from 'react-bootstrap'
import { useMediaQuery } from 'react-responsive'
import parse from 'html-react-parser'
import cn from 'classnames'

import { getMeditationData } from 'components/common/api/Practices'
import { ROUTE_ACTIVITIES_MEDITATION_ALL } from '../../../common/helpers/Paths'
import useFetchByID from '../../../common/hooks/useFetchByID'
import Loading from '../../../../templates/Loading'
import meditationBackground from 'assets/images/waimakariri.svg'
import CustomRangeSlider from '../CustomRangeSlider'
import WebAudioPlayer from '../WebAudioPlayer'
import { isInApp } from '../../../common/helpers/Mobile'

export default function Meditations({
  setReturnPath,
  setHeaderTitle,
  history
}) {
  const isLargeScreen = useMediaQuery({ minWidth: 576 })
  const historyState = history.location.state

  const { meditationId } = useParams()
  const { data, isLoaded } = useFetchByID(getMeditationData, meditationId)

  const [musicVolume, setMusicVolume] = useState(0)
  const [bgVolume, setBgVolume] = useState(0)
  const [volume, setVolume] = useState(0)

  const audioSources = {
    spoken: data?.acf?.spoken_track_url,
    bgMusic: data?.acf?.music_track_url
  }

  const handleVolume = value => setVolume(value)

  const handleMusicVolume = value => setMusicVolume(value)

  const handleBgVolume = value => setBgVolume(value)

  // Set header and return path
  useEffect(() => {
    setHeaderTitle(null)
    setReturnPath(
      meditationId
        ? `${ROUTE_ACTIVITIES_MEDITATION_ALL}/intro/${meditationId}`
        : ROUTE_ACTIVITIES_MEDITATION_ALL
    )

    !meditationId && setVolume(historyState.volume)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeaderTitle, setReturnPath, meditationId])

  useEffect(() => {
    const footer = document.querySelector('.footer')

    // hide footer
    footer?.classList.add('footer--hidden')
  }, [])

  const handleDisplayVolumeControllers = () => {
    const meditationDrawer = document.querySelector('.meditation-drawer')

    meditationDrawer &&
      meditationDrawer.classList.toggle('meditation-drawer--visible')
  }

  return (
    <Loading isLoaded={!isLoaded}>
      <div className='meditation-player-container'>
        <img
          src={meditationBackground}
          alt='meditation background'
          className='orbs-image'
          fetchpriority='high'
        />
        <main className='meditation-player-main-content px-4 pt-4 d-flex flex-column justify-content-around'>
          <h1 className='text-white meditation-player--header mt-2 pt-5 px-2'>
            {data.title?.rendered && parse(data.title.rendered)}
          </h1>
          <section
            className={cn(
              'audio-player-actions position-relative mb-0 mt-auto',
              {
                'audio-player-actions--large': isLargeScreen
              }
            )}
          >
            {/* Don't show volume controls if the user is on a mobile device, because the device has hardware controls */}
            {!isInApp() && (
              <>
                <button
                  className='p-0 border-0 bg-transparent text-white'
                  type='button'
                  onClick={handleDisplayVolumeControllers}
                >
                  <i className='bi bi-volume-up fs-4' />
                </button>
                <Drawer
                  duration={250}
                  hideScrollbars={true}
                  className='meditation-drawer'
                  isVisible={true}
                >
                  <div className='meditation-drawer-container d-flex flex-column pb-4 pt-2 px-3'>
                    {!meditationId ? (
                      <div className='music-volume-slider-container'>
                        <span className='volume-slider-text d-block text-left'>
                          Volume
                        </span>
                        <div className='d-flex gap-sm-4 align-items-center mt-sm-2 justify-content-between'>
                          <div>
                            {volume > 0 ? (
                              <button className='volume-up-button mt-3 mt-sm-0 p-0 border-0 bg-transparent'>
                                <i className='bi bi-volume-up'></i>
                              </button>
                            ) : (
                              <button className='volume-mute-button mt-3 mt-sm-0 p-0 border-0 bg-transparent'>
                                <i className='bi bi-volume-mute'></i>
                              </button>
                            )}
                          </div>
                          <div className='me-4 me-sm-0'>
                            <CustomRangeSlider handleVolume={handleVolume} />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className='music-volume-slider-container'>
                          <span className='volume-slider-text d-block text-left'>
                            Music Volume
                          </span>
                          <div className='d-flex gap-sm-4 align-items-center mt-sm-2 justify-content-between'>
                            <div>
                              {musicVolume > 0 ? (
                                <button className='volume-up-button mt-3 mt-sm-0 p-0 border-0 bg-transparent'>
                                  <i className='bi bi-volume-up'></i>
                                </button>
                              ) : (
                                <button className='volume-mute-button mt-3 mt-sm-0 p-0 border-0 bg-transparent'>
                                  <i className='bi bi-volume-mute'></i>
                                </button>
                              )}
                            </div>
                            <div className='me-4 me-sm-0'>
                              <CustomRangeSlider
                                handleMusicVolume={handleMusicVolume}
                              />
                            </div>
                          </div>
                        </div>
                        <div className='bg-volume-slider-container mt-4'>
                          <span className='volume-slider-text d-block text-left'>
                            Volume
                          </span>
                          <div className='d-flex gap-sm-4 align-items-center mt-sm-2 justify-content-between'>
                            <div>
                              {bgVolume > 0 ? (
                                <button className='volume-up-button mt-3 mt-sm-0 p-0 border-0 bg-transparent'>
                                  <i className='bi bi-volume-up'></i>
                                </button>
                              ) : (
                                <button className='volume-mute-button mt-3 mt-sm-0 p-0 border-0 bg-transparent'>
                                  <i className='bi bi-volume-mute'></i>
                                </button>
                              )}
                            </div>
                            <div className='me-4 me-sm-0'>
                              <CustomRangeSlider
                                handleBgVolume={handleBgVolume}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    <Button
                      className='meditation-content-call-for-action w-100 mt-5'
                      onClick={handleDisplayVolumeControllers}
                      size='lg'
                    >
                      Change
                    </Button>
                  </div>
                </Drawer>
              </>
            )}
          </section>

          <div
            className={cn('meditation-audio-player', {
              'meditation-audio-player--app': isInApp()
            })}
          >
            {meditationId ? (
              <WebAudioPlayer
                audioSources={audioSources}
                musicVolume={musicVolume}
                bgVolume={bgVolume}
              />
            ) : (
              <WebAudioPlayer
                singleAudio={true}
                audioSources={{ singleAudio: historyState.sound }}
                volume={volume}
                duration={historyState.duration}
              />
            )}
          </div>
        </main>
      </div>
    </Loading>
  )
}
